import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX } from 'react-icons/fi';
import logo from '../assets/logo.png';

const NAV = [
  { id: 'home',       label: 'Home' },
  { id: 'solutions',  label: 'Solutions' },
  { id: 'products',   label: 'Products' },
  { id: 'industries', label: 'Industries' },
  { id: 'about',      label: 'About SPE' },
  { id: 'contact',    label: 'Contact' },
];

export default function Navbar() {
  const [scrolled,  setScrolled]  = useState(false);
  const [visible,   setVisible]   = useState(true);
  const [open,      setOpen]      = useState(false);
  const [active,    setActive]    = useState('home');

  const lastY    = useRef(0);
  const heroH    = useRef(0);

  useEffect(() => {
    /* measure hero height once */
    const hero = document.getElementById('home');
    if (hero) heroH.current = hero.offsetHeight;

    const onScroll = () => {
      const y = window.scrollY;
      const d = document.documentElement;

      /* progress bar */
      const bar = document.getElementById('scroll-bar');
      if (bar) {
        bar.style.width = ((d.scrollTop / (d.scrollHeight - d.clientHeight)) * 100) + '%';
      }

      /* scrolled style */
      setScrolled(y > 40);

      /* hide/show logic:
         - always show inside the hero section
         - once past the hero, hide while scrolling down, show while scrolling up */
      if (y <= heroH.current) {
        setVisible(true);
      } else {
        const goingDown = y > lastY.current;
        setVisible(!goingDown);
      }
      lastY.current = y;

      /* active section highlight */
      NAV.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) {
          const { top, bottom } = el.getBoundingClientRect();
          if (top <= 110 && bottom >= 110) setActive(id);
        }
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = id => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setOpen(false);
    setActive(id);
  };

  return (
    <>
      <div id="scroll-bar" />

      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{
          y:       visible ? 0 : -100,
          opacity: visible ? 1 : 0,
        }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        style={{
          position:     'fixed',
          top:          0,
          left:         0,
          right:        0,
          zIndex:       1000,
          padding:      scrolled ? '0.5rem var(--px,20px)' : '0.85rem var(--px,20px)',
          background:   scrolled ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0)',
          backdropFilter: scrolled ? 'blur(24px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(233,30,140,0.1)' : 'none',
          boxShadow:    scrolled ? '0 4px 30px rgba(233,30,140,0.07)' : 'none',
          transition:   'padding 0.4s, background 0.4s, box-shadow 0.4s',
        }}
      >
        <div style={{
          maxWidth:       'var(--max-w,1280px)',
          margin:         '0 auto',
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          gap:            '1rem',
        }}>

          {/* ── Logo ── */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => go('home')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                     display: 'flex', alignItems: 'center', gap: '0.6rem', flexShrink: 0 }}
          >
            <div style={{ position: 'relative' }}>
              <img
                src={logo} alt="SPE Logo"
                style={{ width: 46, height: 46, borderRadius: '50%',
                         objectFit: 'cover', position: 'relative', zIndex: 2 }}
              />
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
                style={{
                  position: 'absolute', inset: -3, borderRadius: '50%',
                  background: 'conic-gradient(from 0deg,#E91E8C,#FF6B2B,#FFD700,transparent,#E91E8C)',
                  opacity: 0.7, zIndex: 1,
                }}
              />
            </div>
          </motion.button>

          {/* ── Desktop nav ── */}
          <div className="nav-desk" style={{
            display: 'flex', alignItems: 'center',
            gap: '0.25rem', flex: 1, justifyContent: 'center',
          }}>
            {NAV.map(({ id, label }) => (
              <motion.button
                key={id}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => go(id)}
                style={{
                  background: active === id
                    ? 'linear-gradient(135deg,rgba(233,30,140,0.09),rgba(255,107,43,0.06))'
                    : 'transparent',
                  border: active === id
                    ? '1px solid rgba(233,30,140,0.22)'
                    : '1px solid transparent',
                  borderRadius: 8,
                  padding:    '0.42rem 0.95rem',
                  cursor:     'pointer',
                  fontFamily: 'var(--font-body)',
                  fontWeight: 600,
                  fontSize:   '0.83rem',
                  color:      active === id ? '#E91E8C' : 'var(--text-mid)',
                  transition: 'all 0.25s',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </motion.button>
            ))}
          </div>

          {/* ── Get Quote (desktop) ── */}
          <motion.button
            className="nav-quote-desk"
            whileHover={{ scale: 1.05, boxShadow: '0 10px 30px rgba(249,115,22,0.4)' }}
            whileTap={{ scale: 0.95 }}
            onClick={() => go('contact')}
            style={{
              display:    'inline-flex',
              alignItems: 'center',
              gap:        '0.4rem',
              padding:    '0.52rem 1.35rem',
              borderRadius: 999,
              background: 'linear-gradient(135deg,#F97316,#EA580C)',
              color:      '#fff',
              border:     'none',
              cursor:     'pointer',
              fontFamily: 'var(--font-body)',
              fontWeight: 700,
              fontSize:   '0.82rem',
              boxShadow:  '0 4px 20px rgba(249,115,22,0.35)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            Get Quote
          </motion.button>

          {/* ── Hamburger (mobile) ── */}
          <motion.button
            className="nav-mob"
            whileTap={{ scale: 0.9 }}
            onClick={() => setOpen(o => !o)}
            style={{
              background:  'rgba(233,30,140,0.06)',
              border:      '1px solid rgba(233,30,140,0.2)',
              borderRadius: 10,
              padding:     '0.5rem',
              cursor:      'pointer',
              color:       '#E91E8C',
              display:     'flex',
              alignItems:  'center',
              justifyContent: 'center',
              flexShrink:  0,
            }}
          >
            {open ? <FiX size={22} /> : <FiMenu size={22} />}
          </motion.button>
        </div>

        {/* ── Mobile drawer ── */}
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{   opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                overflow:       'hidden',
                background:     'rgba(255,255,255,0.98)',
                backdropFilter: 'blur(24px)',
                borderTop:      '1px solid rgba(233,30,140,0.08)',
                marginTop:      '0.5rem',
              }}
            >
              {NAV.map(({ id, label }, i) => (
                <motion.button
                  key={id}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0,   opacity: 1 }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => go(id)}
                  style={{
                    display:    'block',
                    width:      '100%',
                    textAlign:  'left',
                    padding:    '0.9rem var(--px,20px)',
                    background: 'none',
                    border:     'none',
                    borderBottom: '1px solid rgba(233,30,140,0.06)',
                    fontFamily: 'var(--font-body)',
                    fontWeight: 500,
                    fontSize:   '0.95rem',
                    color:      active === id ? '#E91E8C' : 'var(--text)',
                    cursor:     'pointer',
                  }}
                >
                  {label}
                </motion.button>
              ))}
              <div style={{ padding: '1rem var(--px,20px)' }}>
                <button
                  onClick={() => go('contact')}
                  style={{
                    width:      '100%',
                    padding:    '0.9rem',
                    background: 'linear-gradient(135deg,#F97316,#EA580C)',
                    color:      '#fff',
                    border:     'none',
                    borderRadius: 12,
                    cursor:     'pointer',
                    fontFamily: 'var(--font-body)',
                    fontWeight: 700,
                    fontSize:   '0.95rem',
                  }}
                >
                  Get a Quote
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      <style>{`
        /* desktop: show nav links, hide hamburger */
        @media (min-width: 1025px) {
          .nav-mob          { display: none !important; }
          .nav-desk         { display: flex !important; }
          .nav-quote-desk   { display: inline-flex !important; }
        }

        /* tablet: collapse nav links, show hamburger, keep quote btn */
        @media (max-width: 1024px) {
          .nav-desk         { display: none !important; }
          .nav-mob          { display: flex !important; }
          .nav-quote-desk   { display: none !important; }
        }
      `}</style>
    </>
  );
}